import React, { useState, useEffect } from 'react';
import {
    ChevronLeft,
    ChevronDown,
    ChevronUp,
    Navigation,
    FileImage,
} from 'lucide-react';
import { Camera, ImagePlus } from 'lucide-react';
import { WorkOrder } from '../../types/workOrders';
import { format, parse } from 'date-fns';
import { createGoogleMapsUrl } from '../../utils/maps';
import { TaskList } from './TaskList';
import { JobNotesPage } from './JobNotesPage';
import { PhotoCaptureDialog } from './photo-capture/PhotoCaptureDialog';
import { UnflaggedPhotosPage } from './photo-capture/UnflaggedPhotosPage';
import { useAuthStore } from '../../store/authStore';
import { getWorkOrder, getBidPhotos } from '../../utils/api';
import {
    getWorkOrderDetails,
    saveWorkOrderDetails,
} from '../../utils/offlineDb';
import { toast } from 'react-hot-toast';
import { ProgressPhotos } from './photo-capture/ProgressPhotos';

interface WorkOrderDetailProps {
    workOrder: WorkOrder;
    onBack: () => void;
}

interface InfoRowProps {
    label: string;
    value: string;
    action?: React.ReactNode;
}

function InfoRow({ label, value, action }: InfoRowProps) {
    return (
        <div className='py-3 border-b border-light-border dark:border-dark-border last:border-0'>
            <div className='text-sm text-gray-500 dark:text-gray-400'>
                {label}
            </div>
            <div className='flex justify-between items-center'>
                <div className='text-gray-900 dark:text-gray-100'>
                    {value || 'N/A'}
                </div>
                {action}
            </div>
        </div>
    );
}

export function WorkOrderDetail({
    workOrder: initialWorkOrder,
    onBack,
}: WorkOrderDetailProps) {
    const [workOrder, setWorkOrder] = useState(initialWorkOrder);
    const [isCommentsExpanded, setIsCommentsExpanded] = useState(false);
    const [needsTruncation, setNeedsTruncation] = useState(false);
    const [showJobNotes, setShowJobNotes] = useState(false);
    const [showPhotoCapture, setShowPhotoCapture] = useState(false);
    const [showUnflaggedPhotos, setShowUnflaggedPhotos] = useState(false);
    const [showProgressPhotos, setShowProgressPhotos] = useState(false);
    const { accessToken } = useAuthStore();
    const [refreshKey, setRefreshKey] = useState(0);
    const [hasUnflaggedPhotos, setHasUnflaggedPhotos] = useState(false);

    const handlePhotoTaken = async (file: File, taskItem: string) => {
        if (!accessToken) return;

        const formData = new FormData();
        formData.append('photos', file);
        formData.append('taskItem', taskItem);

        try {
            const response = await fetch(
                `https://api.fieldy.io/bid-photos/${workOrder.report_id}`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: formData,
                },
            );

            if (!response.ok) {
                throw new Error('Failed to upload photo');
            }

            toast.success('Photo uploaded successfully');
            setShowPhotoCapture(false);
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload photo');
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [refreshKey]);

    useEffect(() => {
        if (workOrder.comments) {
            setNeedsTruncation(workOrder.comments.length > 150);
        }
    }, [workOrder.comments]);

    useEffect(() => {
        const fetchWorkOrder = async () => {
            if (!accessToken) return;

            const cachedDetails = await getWorkOrderDetails(
                workOrder.report_id,
            );
            if (cachedDetails) {
                setWorkOrder((prev) => ({
                    ...prev,
                    job_notes: cachedDetails.jobNotes || [],
                    bidPhotos: cachedDetails.bidPhotos || [],
                    cached: true,
                }));
            }

            if (!navigator.onLine) {
                if (!cachedDetails) {
                    console.log('No cached data available offline');
                }
                return;
            }

            try {
                const [data, photos] = await Promise.all([
                    getWorkOrder(accessToken, workOrder.report_id),
                    getBidPhotos(workOrder.report_id),
                ]);

                await saveWorkOrderDetails(
                    workOrder.report_id,
                    data.job_notes || [],
                    photos || [],
                );

                setWorkOrder((prev) => ({
                    ...prev,
                    job_notes: data.job_notes || [],
                    bidPhotos: photos || [],
                    cached: false,
                }));
            } catch (error) {
                if (!cachedDetails) {
                    console.error('No cached data available');
                }
            }
        };
        fetchWorkOrder();
    }, [accessToken, workOrder.report_id, refreshKey]);

    useEffect(() => {
        const checkUnflaggedPhotos = async () => {
            if (!accessToken) return;

            try {
                const photos = await getBidPhotos(workOrder.report_id);
                const unflagged = photos.filter(
                    (photo) => photo.taskItem === 'Unflagged photos',
                );
                setHasUnflaggedPhotos(unflagged.length > 0);
            } catch (error) {
                console.error('Error checking unflagged photos:', error);
            }
        };

        checkUnflaggedPhotos();
    }, [accessToken, workOrder.report_id]);

    const handleNoteAdded = async () => {
        setRefreshKey((prev) => prev + 1);
    };

    const formatDate = (dateStr: string) => {
        try {
            const date = parse(dateStr, 'yyyyMMdd', new Date());
            return format(date, 'MMM d, yyyy');
        } catch {
            return 'N/A';
        }
    };

    const truncateComments = (comments: string) => {
        if (!needsTruncation) return comments;
        return `${comments.slice(0, 150)}...`;
    };

    const handleGetDirections = () => {
        window.open(
            createGoogleMapsUrl(workOrder.address, workOrder.city),
            '_blank',
        );
    };

    if (showJobNotes) {
        return (
            <JobNotesPage
                reportId={workOrder.report_id}
                woNumber={workOrder.wo_number}
                onBack={() => setShowJobNotes(false)}
                notes={workOrder.job_notes || []}
                onNoteAdded={handleNoteAdded}
            />
        );
    }

    if (showUnflaggedPhotos) {
        return (
            <UnflaggedPhotosPage
                reportId={workOrder.report_id}
                woNumber={workOrder.wo_number}
                onBack={() => setShowUnflaggedPhotos(false)}
            />
        );
    }

    if (showProgressPhotos) {
        return (
            <ProgressPhotos
                reportId={workOrder.report_id}
                woNumber={workOrder.wo_number}
                onBack={() => setShowProgressPhotos(false)}
            />
        );
    }

    if (showPhotoCapture) {
        return (
            <PhotoCaptureDialog
                reportId={workOrder.report_id}
                tasks={workOrder.Tasks}
                workOrder={workOrder}
                onBack={() => setShowPhotoCapture(false)}
                onPhotoTaken={handlePhotoTaken}
            />
        );
    }

    return (
        <div className='space-y-6'>
            {/* Header */}
            <div className='flex flex-col sm:flex-row gap-4'>
                <div className='flex items-center gap-4'>
                    <button
                        onClick={onBack}
                        className='p-2 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors'
                    >
                        <ChevronLeft className='h-6 w-6 text-gray-600 dark:text-gray-400' />
                    </button>
                    <div>
                        <h2 className='text-xl font-semibold text-gray-900 dark:text-gray-100'>
                            WO #{workOrder.wo_number}
                        </h2>
                        <p className='text-gray-600 dark:text-gray-400'>
                            {workOrder.work_type_alias}
                        </p>
                    </div>
                </div>
                <div className='flex items-center gap-3 sm:ml-auto'>
                    {hasUnflaggedPhotos && (
                        <button
                            onClick={() => setShowUnflaggedPhotos(true)}
                            className='btn-secondary flex items-center gap-2 relative'
                            title='View unflagged photos'
                        >
                            <ImagePlus className='h-4 w-4' />
                            <div className='absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse' />
                        </button>
                    )}
                    <button
                        onClick={() => setShowProgressPhotos(true)}
                        className='btn-secondary flex items-center gap-2'
                    >
                        <FileImage className='h-4 w-4' />
                    </button>
                    <button
                        onClick={() => setShowPhotoCapture(true)}
                        className='btn-secondary flex items-center gap-2'
                    >
                        <Camera className='h-4 w-4' />
                        Take Photos
                    </button>
                    <button
                        onClick={() => setShowJobNotes(true)}
                        className='btn-secondary flex items-center gap-2'
                    >
                        Job Notes ({workOrder.job_notes?.length || 0})
                    </button>
                </div>
            </div>

            {/* Work Order Information */}
            <div className='bg-light-card dark:bg-dark-card rounded-lg shadow-soft dark:shadow-soft-dark p-6 space-y-4 border border-light-border dark:border-dark-border'>
                <InfoRow
                    label='Client Company'
                    value={workOrder.client_company_alias}
                />
                <InfoRow label='Customer' value={workOrder.cust_text} />
                <InfoRow label='Loan #' value={workOrder.loan_number} />
                <InfoRow label='Loan Type' value={workOrder.loan_type_other} />
                <InfoRow
                    label='Address'
                    value={workOrder.address}
                    action={
                        <button
                            onClick={handleGetDirections}
                            className='flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mt-2 sm:mt-0'
                        >
                            <Navigation className='h-4 w-4' />
                            <span className='text-sm'>Get Directions</span>
                        </button>
                    }
                />
                <InfoRow
                    label='City/State'
                    value={`${workOrder.city}, ${workOrder.state} ${workOrder.zip}`}
                />
                <InfoRow label='Mortgagor' value={workOrder.mortgage_name} />
                <InfoRow
                    label='Lock/Key code'
                    value={`${workOrder.lock_code}/${workOrder.key_code}`}
                />
                <InfoRow label='Lot Size' value={workOrder.lot_size} />
                <InfoRow
                    label='Due Date'
                    value={formatDate(workOrder.date_due)}
                />
                <InfoRow label='Status' value={workOrder.status} />
            </div>

            {/* Comments Section */}
            {workOrder.comments && (
                <div className='bg-light-card dark:bg-dark-card rounded-lg shadow-soft dark:shadow-soft-dark p-6 border border-light-border dark:border-dark-border'>
                    <div className='flex justify-between items-center mb-4'>
                        <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
                            Comments
                        </h3>
                        {needsTruncation && (
                            <button
                                onClick={() =>
                                    setIsCommentsExpanded(!isCommentsExpanded)
                                }
                                className='text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1'
                            >
                                {isCommentsExpanded ? (
                                    <>
                                        <ChevronUp className='h-4 w-4' />
                                        Show Less
                                    </>
                                ) : (
                                    <>
                                        <ChevronDown className='h-4 w-4' />
                                        Show More
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    <div
                        className='text-gray-600 dark:text-gray-400'
                        dangerouslySetInnerHTML={{
                            __html: isCommentsExpanded
                                ? workOrder.comments
                                : truncateComments(workOrder.comments),
                        }}
                    />
                </div>
            )}

            {/* Tasks Section */}
            <div className='bg-light-card dark:bg-dark-card rounded-lg shadow-soft dark:shadow-soft-dark p-6 border border-light-border dark:border-dark-border'>
                <TaskList
                    tasks={workOrder.Tasks}
                    workOrder={workOrder}
                    onTaskUpdate={() => {}}
                />
            </div>
        </div>
    );
}
